import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { 
  X, Building2, MapPin, Phone, UploadCloud, 
  Search, FileDigit, Mail, User, Trash2, Eye, Download, Loader2, Edit3
} from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";

import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { collection, doc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, storage, storageEnvironment } from "../../config/firebase";

const DOCUMENT_TYPES = [
  "CI dos administradores",
  "Contrato Social",
  "Cartão CNPJ",
  "Comprovante de Endereço",
  "Inscrição Estadual/Municipal",
  "Outros"
];

const BRAZILIAN_STATES = [
  { value: "AC", label: "Acre" }, { value: "AL", label: "Alagoas" }, { value: "AP", label: "Amapá" },
  { value: "AM", label: "Amazonas" }, { value: "BA", label: "Bahia" }, { value: "CE", label: "Ceará" },
  { value: "DF", label: "Distrito Federal" }, { value: "ES", label: "Espírito Santo" }, { value: "GO", label: "Goiás" },
  { value: "MA", label: "Maranhão" }, { value: "MT", label: "Mato Grosso" }, { value: "MS", label: "Mato Grosso do Sul" },
  { value: "MG", label: "Minas Gerais" }, { value: "PA", label: "Pará" }, { value: "PB", label: "Paraíba" },
  { value: "PR", label: "Paraná" }, { value: "PE", label: "Pernambuco" }, { value: "PI", label: "Piauí" },
  { value: "RJ", label: "Rio de Janeiro" }, { value: "RN", label: "Rio Grande do Norte" }, { value: "RS", label: "Rio Grande do Sul" },
  { value: "RO", label: "Rondônia" }, { value: "RR", label: "Roraima" }, { value: "SC", label: "Santa Catarina" },
  { value: "SP", label: "São Paulo" }, { value: "SE", label: "Sergipe" }, { value: "TO", label: "Tocantins" }
];

// --- FUNÇÃO AUXILIAR: LIMPADOR DE UNDEFINED ---
/** @param {any} obj */
const cleanPayload = (obj) => {
  return JSON.parse(JSON.stringify(obj, (key, value) => (value === undefined ? null : value)));
};

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {any} [props.companyToEdit]
 */
const CompanyModal = ({ isOpen, onClose, companyToEdit }) => {
  const brandColor = "#00a99d";
  const isEditing = !!companyToEdit;

  const [selectedFiles, setSelectedFiles] = useState(/** @type {any[]} */ ([]));
  const [isFetchingCep, setIsFetchingCep] = useState(false);
  const [isFetchingCnpj, setIsFetchingCnpj] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditing && companyToEdit?.documents) {
        setSelectedFiles(companyToEdit.documents);
      } else {
        setSelectedFiles([]);
      }
    }
  }, [isOpen, isEditing, companyToEdit]);

  const formik = useFormik({
    initialValues: {
      codigo: companyToEdit?.codigo || "",
      nome: companyToEdit?.nome || "",
      nomeFantasia: companyToEdit?.nomeFantasia || "",
      cnpj: companyToEdit?.cnpj || "",
      creci: companyToEdit?.creci || "",
      cep: companyToEdit?.cep || "",
      endereco: companyToEdit?.endereco || "",
      numero: companyToEdit?.numero || "",
      complemento: companyToEdit?.complemento || "",
      bairro: companyToEdit?.bairro || "",
      estado: companyToEdit?.estado || "",
      cidade: companyToEdit?.cidade || "",
      telefone: companyToEdit?.telefone || "",
      email: companyToEdit?.email || "",
      contato: companyToEdit?.contato || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      nome: Yup.string().required("Nome obrigatório"),
      cnpj: Yup.string().required("CNPJ obrigatório"),
      email: Yup.string().email("E-mail inválido").required("E-mail obrigatório"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);
      setIsUploading(true);
      
      /** @type {any[]} */
      const uploadedStorageRefs = [];
      
      try {
        const finalDocuments = [];

        const companyRef = isEditing
          ? doc(db, "companies", companyToEdit.id)
          : doc(collection(db, "companies"));

        const companyId = companyRef.id;
        
        // FASE 1: UPLOAD DOS ARQUIVOS
        for (const item of selectedFiles) {
          if (item.file) { 
            const storageRef = ref(
              storage,
              `companiesDocs/${storageEnvironment}/${companyId}/${Date.now()}_${item.name}`
            );
            
            try {
              const snapshot = await uploadBytes(storageRef, item.file);
              uploadedStorageRefs.push(storageRef);
              
              const downloadURL = await getDownloadURL(snapshot.ref);
              
              finalDocuments.push({
                id: item.id,
                name: item.name,
                type: item.type,
                observation: item.observation,
                url: downloadURL,
                uploadedAt: new Date().toISOString()
              });
            } catch (uploadError) {
              console.error(`Erro no upload do arquivo ${item.name}:`, uploadError);
              throw new Error(`Falha ao enviar o arquivo ${item.name}. Verifique sua conexão e tente novamente.`);
            }
          } else {
            finalDocuments.push(item);
          }
        }

        const companyData = cleanPayload({
          ...values,
          documents: finalDocuments
        });

        // FASE 2: GRAVAÇÃO NO BANCO DE DADOS (Com Timeout)
        const firestorePromise = isEditing
          ? updateDoc(companyRef, companyData)
          : setDoc(companyRef, {
              ...companyData,
              status: "active",
              createdAt: serverTimestamp(),
              memberUids: [],
              planoId: "Básico"
            });

        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error("TIMEOUT_FIRESTORE")), 10000)
        );

        try {
          await Promise.race([firestorePromise, timeoutPromise]);
        } catch (error) {
          const dbError = /** @type {Error} */ (error);
          if (dbError.message === "TIMEOUT_FIRESTORE") {
             throw new Error("Tempo limite excedido ao conectar com o banco de dados. A operação foi cancelada.");
          }
          console.error("Erro no Firestore:", dbError);
          throw new Error(`Falha ao salvar no banco: ${dbError.message}`);
        }
        
        // FASE 3: SUCESSO
        resetForm();
        setSelectedFiles([]);
        onClose();

      } catch (error) {
        const catchedError = /** @type {Error} */ (error);
        console.error("Erro na transação de salvar empresa:", catchedError);
        alert(catchedError.message);

        // FASE 4: ROLLBACK SILENCIOSO
        if (uploadedStorageRefs.length > 0) {
          for (const sRef of uploadedStorageRefs) {
            try {
              await deleteObject(sRef);
            } catch (rollbackError) {
              console.error("Erro durante o Rollback de arquivos:", rollbackError);
            }
          }
        }
      } finally {
        setSubmitting(false);
        setIsUploading(false);
      }
    },
  });

  const { setFieldValue } = formik;
  useEffect(() => {
    if (isOpen && !isEditing) {
      const generatedCode = Math.floor(10000 + Math.random() * 90000).toString();
      setFieldValue("codigo", generatedCode);
    }
  }, [isOpen, isEditing, setFieldValue]);

  const handleCnpjSearch = async () => {
    const cnpjLimpo = formik.values.cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      alert("Digite um CNPJ válido com 14 dígitos.");
      return;
    }

    setIsFetchingCnpj(true);
    try {
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`);
      if (!response.ok) throw new Error("Erro na busca");
      
      const data = await response.json();

      formik.setValues({
        ...formik.values,
        nome: data.razao_social || "",
        nomeFantasia: data.nome_fantasia || "", 
        cep: data.cep || "",
        endereco: data.logradouro || "",
        numero: data.numero || "",
        complemento: data.complemento || "",
        bairro: data.bairro || "",
        cidade: data.municipio || "",
        estado: data.uf || "",
        email: data.email || formik.values.email,
        telefone: data.ddd_telefone_1 || formik.values.telefone,
      });

    } catch (error) {
      console.error("Erro ao buscar CNPJ:", error);
      alert("Não foi possível localizar os dados deste CNPJ.");
    } finally {
      setIsFetchingCnpj(false);
    }
  };

  const handleCepSearch = async () => {
    const cepNumeros = formik.values.cep?.replace(/\D/g, '');
    if (!cepNumeros || cepNumeros.length !== 8) return;

    setIsFetchingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepNumeros}/json/`);
      const data = await response.json();
      if (!data.erro) {
        formik.setFieldValue("endereco", data.logradouro || "");
        formik.setFieldValue("bairro", data.bairro || "");
        formik.setFieldValue("cidade", data.localidade || "");
        formik.setFieldValue("estado", data.uf || "");
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    } finally {
      setIsFetchingCep(false);
    }
  };

  /** @param {any} event */
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    const newFiles = files.map(file => ({
      file: file, 
      id: Math.random().toString(36).substring(7), 
      // @ts-ignore
      name: file.name,
      type: "Contrato Social", 
      observation: ""
    }));
    setSelectedFiles([...selectedFiles, ...newFiles]);
  };

  /** @param {any} fileIdToRemove */
  const handleRemoveFile = (fileIdToRemove) => {
    setSelectedFiles(selectedFiles.filter(f => f.id !== fileIdToRemove));
  };

  /** * @param {any} fileId 
   * @param {any} newType 
   */
  const handleUpdateFileType = (fileId, newType) => {
    setSelectedFiles(selectedFiles.map(f => 
      f.id === fileId ? { ...f, type: newType } : f
    ));
  };

  /** * @param {any} fileId 
   * @param {any} newObs 
   */
  const handleUpdateFileObs = (fileId, newObs) => {
    setSelectedFiles(selectedFiles.map(f => 
      f.id === fileId ? { ...f, observation: newObs } : f
    ));
  };

  /** * @param {any} fileId 
   * @param {any} newName 
   */
  const handleUpdateFileName = (fileId, newName) => {
    setSelectedFiles(selectedFiles.map(f => 
      f.id === fileId ? { ...f, name: newName } : f
    ));
  };

  /** @param {any} fileItem */
  const handlePreviewFile = (fileItem) => {
    const url = fileItem.url || (fileItem.file ? URL.createObjectURL(fileItem.file) : null);
    if (url) window.open(url, '_blank');
  };

  if (!isOpen) return null;

  const inputClass = "w-full rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg text-sm p-2.5 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 outline-none transition-colors shadow-sm";

  return ReactDOM.createPortal(
    <div 
      className="flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-4 sm:p-6 lg:p-8"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh', zIndex: 999999 }}
    >
      <div className="relative w-full max-w-5xl flex flex-col bg-white dark:bg-dark-card rounded-2xl shadow-2xl border border-light-border dark:border-dark-border overflow-hidden" style={{ maxHeight: '90vh' }}>
        
        {/* HEADER */}
        <div className="flex-shrink-0 flex items-center justify-between p-5 lg:p-6 border-b border-light-border dark:border-dark-border bg-gray-50/50 dark:bg-dark-bg/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg text-white shadow-sm" style={{ backgroundColor: brandColor }}>
              <Building2 className="w-5 h-5 lg:w-6 lg:h-6" />
            </div>
            <div>
              <h3 className="text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{isEditing ? 'Editar Imobiliária' : 'Nova Imobiliária'}</h3>
              <p className="text-xs lg:text-sm text-gray-500 dark:text-gray-400 mt-0.5">Gestão de parceiros da Real Benefícios.</p>
            </div>
          </div>
          <button onClick={() => { formik.resetForm(); setSelectedFiles([]); onClose(); }} className="text-gray-400 hover:text-gray-900 dark:hover:text-white p-2 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-dark-border">
            <X className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
        </div>

        {/* CORPO */}
        <div className="overflow-y-auto p-5 lg:p-8 flex-1 min-h-0 custom-scrollbar bg-white dark:bg-dark-card">
          <form id="company-form" onSubmit={formik.handleSubmit} className="space-y-8 lg:space-y-10">
            
            {/* IDENTIFICAÇÃO */}
            <div>
              <h4 className="flex items-center text-xs lg:text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 lg:mb-5 border-b border-gray-200 dark:border-dark-border pb-2">
                <FileDigit className="w-4 h-4 mr-2" style={{ color: brandColor }} /> Identificação
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-5 mb-4 lg:mb-5">
                <div className="md:col-span-2">
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Código</label>
                  <input type="text" {...formik.getFieldProps("codigo")} readOnly className={`${inputClass} bg-gray-100 dark:bg-dark-bg/50 cursor-not-allowed font-mono text-gray-500`} />
                </div>
                <div className="md:col-span-4">
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">CNPJ <span className="text-red-500">*</span></label>
                  <div className="flex shadow-sm rounded-lg">
                    <input type="text" {...formik.getFieldProps("cnpj")} className={`${inputClass} rounded-r-none border-r-0`} placeholder="00.000.000/0000-00" />
                    <button type="button" onClick={handleCnpjSearch} disabled={isFetchingCnpj} className="px-4 rounded-r-lg border border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-teal-600 hover:bg-teal-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center disabled:opacity-50">
                      {isFetchingCnpj ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="md:col-span-6">
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Razão Social <span className="text-red-500">*</span></label>
                  <input type="text" {...formik.getFieldProps("nome")} className={inputClass} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-5">
                <div className="md:col-span-8">
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Nome Fantasia</label>
                  <input type="text" {...formik.getFieldProps("nomeFantasia")} placeholder="Ex: Imobiliária Silva" className={inputClass} />
                </div>
                <div className="md:col-span-4">
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">CRECI</label>
                  <input type="text" {...formik.getFieldProps("creci")} className={inputClass} />
                </div>
              </div>
            </div>

            {/* ENDEREÇO */}
            <div>
              <h4 className="flex items-center text-xs lg:text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 lg:mb-5 border-b border-gray-200 dark:border-dark-border pb-2">
                <MapPin className="w-4 h-4 mr-2" style={{ color: brandColor }} /> Endereço
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-5 mb-4 lg:mb-5">
                <div className="md:col-span-3">
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">CEP</label>
                  <div className="flex shadow-sm rounded-lg">
                    <input type="text" {...formik.getFieldProps("cep")} onBlur={handleCepSearch} className={`${inputClass} rounded-r-none border-r-0`} maxLength={9} placeholder="00000-000" />
                    <button type="button" onClick={handleCepSearch} disabled={isFetchingCep} className="px-4 rounded-r-lg border border-gray-300 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-teal-600 hover:bg-teal-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center">
                      {isFetchingCep ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="md:col-span-5"><label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Logradouro / Endereço</label><input type="text" {...formik.getFieldProps("endereco")} className={inputClass} /></div>
                <div className="md:col-span-2"><label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Número</label><input type="text" {...formik.getFieldProps("numero")} className={inputClass} /></div>
                <div className="md:col-span-2"><label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Comp.</label><input type="text" {...formik.getFieldProps("complemento")} className={inputClass} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-5">
                <div className="md:col-span-4"><label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Bairro</label><input type="text" {...formik.getFieldProps("bairro")} className={inputClass} /></div>
                <div className="md:col-span-4"><label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Estado</label>
                  <select {...formik.getFieldProps("estado")} className={inputClass}>
                    <option value="">Selecione um estado...</option>
                    {BRAZILIAN_STATES.map(uf => <option key={uf.value} value={uf.value}>{uf.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-4"><label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Cidade</label><input type="text" {...formik.getFieldProps("cidade")} className={inputClass} /></div>
              </div>
            </div>

            {/* CONTATO */}
            <div>
              <h4 className="flex items-center text-xs lg:text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 lg:mb-5 border-b border-gray-200 dark:border-dark-border pb-2">
                <Phone className="w-4 h-4 mr-2" style={{ color: brandColor }} /> Contato Principal
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                <div>
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Telefone / WhatsApp</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Phone className="h-4 w-4 text-gray-400" /></div>
                    <input type="text" {...formik.getFieldProps("telefone")} className={`${inputClass} pl-10`} placeholder="(00) 00000-0000" />
                  </div>
                </div>
                <div>
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">E-mail <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Mail className="h-4 w-4 text-gray-400" /></div>
                    <input type="email" {...formik.getFieldProps("email")} className={`${inputClass} pl-10`} placeholder="contato@empresa.com.br" />
                  </div>
                </div>
                <div>
                  <label className="block mb-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">Nome do Responsável</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><User className="h-4 w-4 text-gray-400" /></div>
                    <input type="text" {...formik.getFieldProps("contato")} className={`${inputClass} pl-10`} placeholder="João Silva" />
                  </div>
                </div>
              </div>
            </div>

            {/* DOCUMENTOS */}
            <div>
              <h4 className="flex items-center text-xs lg:text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4 lg:mb-5 border-b border-gray-200 dark:border-dark-border pb-2">
                <UploadCloud className="w-4 h-4 mr-2" style={{ color: brandColor }} /> Documentação Institucional
              </h4>
              <div className="w-full flex justify-center px-6 pt-6 pb-8 border-2 border-gray-300 dark:border-dark-border border-dashed rounded-xl hover:border-teal-500 hover:bg-teal-50/50 dark:hover:bg-slate-800/50 transition-colors bg-gray-50 dark:bg-dark-bg cursor-pointer group mb-6">
                <div className="space-y-2 text-center">
                  <UploadCloud className="mx-auto h-10 w-10 lg:h-12 lg:w-12 text-gray-400 group-hover:text-teal-500 transition-colors" />
                  <div className="flex flex-col sm:flex-row text-xs lg:text-sm text-gray-600 dark:text-gray-400 justify-center items-center gap-1">
                    <label className="relative cursor-pointer rounded-md font-bold text-teal-600 hover:text-teal-700 outline-none">
                      <span>Clique para selecionar os arquivos</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" multiple onChange={handleFileSelect} />
                    </label>
                    <p className="hidden sm:block">ou arraste e solte aqui</p>
                  </div>
                  <p className="text-[10px] lg:text-xs text-gray-500 dark:text-gray-500">Aceitamos PDF, PNG e JPG (Tamanho máx: 10MB)</p>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden shadow-sm mt-4">
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                      <thead className="bg-gray-50 dark:bg-dark-bg/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-[10px] lg:text-xs font-bold text-gray-500 dark:text-gray-400 uppercase w-24 tracking-wider">Ações</th>
                          <th className="px-4 py-3 text-left text-[10px] lg:text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/4">Nome do Arquivo</th>
                          <th className="px-4 py-3 text-left text-[10px] lg:text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/4">Classificação</th>
                          <th className="px-4 py-3 text-left text-[10px] lg:text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-1/3">Observações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                        {selectedFiles.map((fileItem) => (
                          <tr key={fileItem.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg/50 transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-3">
                                <button type="button" onClick={() => handlePreviewFile(fileItem)} className="text-gray-400 hover:text-teal-600 transition-colors" title="Visualizar"><Eye className="w-4 h-4" /></button>
                                <button type="button" className="text-gray-400 hover:text-blue-600 transition-colors" title="Baixar"><Download className="w-4 h-4" /></button>
                                <button type="button" onClick={() => handleRemoveFile(fileItem.id)} className="text-gray-400 hover:text-red-600 transition-colors" title="Remover"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center relative">
                                <input 
                                  type="text" 
                                  value={fileItem.name} 
                                  onChange={(e) => handleUpdateFileName(fileItem.id, e.target.value)} 
                                  className="w-full bg-transparent border-b border-dashed border-gray-300 dark:border-gray-600 text-xs lg:text-sm font-medium text-gray-900 dark:text-gray-200 focus:outline-none focus:border-teal-500 pb-1 pr-6" 
                                  title="Clique para renomear"
                                />
                                <Edit3 className="w-3 h-3 text-gray-400 absolute right-1 bottom-1.5 pointer-events-none" />
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <select value={fileItem.type} onChange={(e) => handleUpdateFileType(fileItem.id, e.target.value)} className={`${inputClass} !p-1.5 text-[10px] lg:text-xs font-medium`}>
                                {DOCUMENT_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
                              </select>
                            </td>
                            <td className="px-4 py-3">
                              <input type="text" value={fileItem.observation} onChange={(e) => handleUpdateFileObs(fileItem.id, e.target.value)} placeholder="Adicionar nota..." className={`${inputClass} !p-1.5 text-[10px] lg:text-xs`} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

          </form>
        </div>

        <div className="flex-shrink-0 flex items-center justify-end space-x-3 lg:space-x-4 p-5 lg:p-6 border-t border-light-border dark:border-dark-border bg-gray-50/50 dark:bg-dark-bg/50">
          <button type="button" onClick={() => { formik.resetForm(); setSelectedFiles([]); onClose(); }} className="px-5 lg:px-6 py-2 lg:py-2.5 text-xs lg:text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors dark:bg-transparent dark:text-gray-300 dark:border-gray-600">Cancelar</button>
          <button type="submit" form="company-form" disabled={formik.isSubmitting} className="px-6 lg:px-8 py-2 lg:py-2.5 text-xs lg:text-sm font-bold text-white rounded-full shadow-md hover:brightness-110 transition-all disabled:opacity-50 flex items-center" style={{ backgroundColor: brandColor }}>
            {isUploading ? <><Loader2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-2 animate-spin"/> Upload...</> : formik.isSubmitting ? <><Loader2 className="w-3.5 h-3.5 lg:w-4 lg:h-4 mr-2 animate-spin"/> Salvando...</> : (isEditing ? "Salvar Alterações" : "Cadastrar Empresa")}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default CompanyModal;